import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    anchors: Array,
    open: Boolean
  }

  initialize() {
    this.openFromLocation = this.openFromLocation.bind(this)
  }

  connect() {
    this.markdown = this.element.querySelector(":scope > .markdown")
    if (!this.markdown) return

    const prepared = this.markdown.dataset.websiteMarkdownReady === "true"

    this.prepareMarkdown()
    window.addEventListener("hashchange", this.openFromLocation)
    if (!prepared || window.location.hash) this.openFromLocation()
  }

  disconnect() {
    window.removeEventListener("hashchange", this.openFromLocation)
  }

  toggle(event) {
    const heading = event.target.closest("h1, h2")
    if (!heading || !this.markdown.contains(heading)) return

    event.preventDefault()

    if (heading.tagName === "H1") {
      this.toggleHeadingOne(heading)
    } else {
      this.setExpanded(heading, !this.expanded(heading))
    }
  }

  toggleWithKeyboard(event) {
    if (!["Enter", " "].includes(event.key)) return

    this.toggle(event)
  }

  prepareMarkdown() {
    if (!this.markdown.dataset.websiteMarkdownReady) {
      this.wrapSections()
      this.markdown.dataset.websiteMarkdownReady = "true"
    }

    this.headingOnes = Array.from(this.markdown.querySelectorAll("h1"))
    this.headingTwos = Array.from(this.markdown.querySelectorAll("h2"))
  }

  wrapSections() {
    const headingOnes = Array.from(this.markdown.querySelectorAll(":scope > h1"))

    headingOnes.forEach((heading, index) => {
      const anchor = this.hasAnchorsValue ? (this.anchorsValue[index] ?? index.toString()) : index.toString()
      const section = this.wrapFollowingSiblings(heading, "H1", "section_h1")

      this.prepareHeading(heading, anchor, section, this.openValue)
      this.wrapHeadingTwoSections(section, anchor)
      section.classList.toggle("has-h2", section.querySelector(".section_h2") !== null)
    })
  }

  wrapHeadingTwoSections(parent, parentAnchor) {
    const headings = Array.from(parent.querySelectorAll(":scope > h2"))

    headings.forEach((heading, index) => {
      const anchor = `${parentAnchor}-${index + 1}`
      const section = this.wrapFollowingSiblings(heading, "H2", "section_h2")

      this.prepareHeading(heading, anchor, section, this.openValue)
    })
  }

  wrapFollowingSiblings(heading, nextHeadingName, className) {
    const section = document.createElement("div")

    section.className = className
    while (heading.nextSibling && heading.nextSibling.nodeName !== nextHeadingName) {
      section.append(heading.nextSibling)
    }
    heading.after(section)

    return section
  }

  prepareHeading(heading, anchor, section, expanded) {
    heading.id = anchor
    heading.setAttribute("name", anchor)
    heading.setAttribute("role", "button")
    heading.setAttribute("tabindex", "0")
    heading.setAttribute("aria-controls", `${anchor}-content`)
    section.id = `${anchor}-content`
    this.setExpanded(heading, expanded)
  }

  toggleHeadingOne(heading) {
    if (!this.openValue) {
      this.headingOnes
        .filter(otherHeading => otherHeading !== heading)
        .forEach(otherHeading => this.setExpanded(otherHeading, false))
    }

    this.setExpanded(heading, !this.expanded(heading))
    this.selectMenuLink(heading.id)
  }

  setExpanded(heading, expanded) {
    const section = heading.nextElementSibling

    heading.classList.toggle("open", expanded)
    heading.setAttribute("aria-expanded", expanded.toString())
    if (section) section.hidden = !expanded
  }

  expanded(heading) {
    return heading.getAttribute("aria-expanded") === "true"
  }

  openFromLocation() {
    if (!this.headingOnes?.length) return

    const anchor = window.location.hash.slice(1)

    if (anchor) {
      this.openAnchor(anchor)
    } else if (this.element.classList.contains("open")) {
      this.setExpanded(this.headingOnes[0], true)
      this.selectMenuLink(this.headingOnes[0].id)
    }
  }

  openAnchor(anchor) {
    const headingTwo = this.headingTwos.find(heading => heading.id === anchor)
    const headingOne = headingTwo
      ? headingTwo.closest(".section_h1")?.previousElementSibling
      : this.headingOnes.find(heading => heading.id === anchor)

    if (!headingOne) return

    if (!this.openValue) {
      this.headingOnes
        .filter(heading => heading !== headingOne)
        .forEach(heading => this.setExpanded(heading, false))
    }

    this.setExpanded(headingOne, true)
    if (headingTwo) this.setExpanded(headingTwo, true)
    this.selectMenuLink(headingOne.id)

    requestAnimationFrame(() => (headingTwo || headingOne).scrollIntoView())
  }

  selectMenuLink(anchor) {
    const links = Array.from(document.querySelectorAll("#menu a[data-anchor]"))
    if (!links.some(link => link.dataset.anchor === `#${anchor}`)) return

    links.forEach(link => {
      link.classList.toggle("current", link.dataset.anchor === `#${anchor}`)
    })
  }
}
